import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { manhwaComics, categories } from '@/mocks/manhwa';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = manhwaComics.find(c => c.slug === id);
  const [expanded, setExpanded] = useState(false);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mt-2">请检查链接是否正确</p>
            <Link to="/" className="mt-6 inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find(c => c.name === comic.category);
  const categorySlug = categoryInfo?.slug || '';

  const relatedComics = manhwaComics
    .filter(c => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const generateDummyChapters = (count: number) => {
    return Array.from({ length: Math.min(count, 10) }, (_, i) => ({
      number: count - i,
      title: `第${count - i}话`,
      date: `2025-0${Math.ceil((i + 1) / 3)}-${String((i * 7) % 28 + 1).padStart(2, '0')}`,
    }));
  };

  const chapters = generateDummyChapters(comic.chapters);

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: comic.category, path: `/category/${categorySlug}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Detail Hero */}
        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              {/* Cover */}
              <div className="shrink-0 mx-auto md:mx-0">
                <div className="w-52 md:w-64 rounded-lg overflow-hidden border border-background-200/70" data-product-shop>
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} 韩国漫画`}
                    title={`${comic.title} - 好看的韩国漫画`}
                    className="w-full aspect-[5/7] object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-400 mt-1">{comic.titleEn}</p>

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-background-300 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                    {comic.category}
                  </span>
                  <span className="flex items-center gap-1 text-sm font-semibold text-accent-600">
                    <i className="ri-star-fill text-sm"></i>
                    {comic.rating}
                  </span>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3 mt-6 p-4 rounded-lg bg-background-100/70 border border-background-200/60">
                  <div>
                    <span className="text-xs text-foreground-400">作者</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.author}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">画师</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.artist}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">总章节</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.chapters}话</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">更新日期</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{comic.updateDate}</p>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map(tag => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-10 max-w-4xl">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">作品简介</h2>
              <div className="relative">
                <p className={`text-sm text-foreground-600 leading-relaxed ${!expanded ? 'line-clamp-3' : ''}`}>
                  {comic.description}
                </p>
                {comic.description.length > 120 && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-sm text-primary-500 font-medium mt-1 hover:text-primary-600"
                  >
                    {expanded ? '收起' : '展开全部'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="text-xs px-3 py-1.5 rounded-full bg-primary-500 text-background-50 font-medium whitespace-nowrap">正序</button>
                <button className="text-xs px-3 py-1.5 rounded-full bg-background-50 text-foreground-500 font-medium border border-background-200/60 whitespace-nowrap">倒序</button>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/70 divide-y divide-background-200/50">
              {chapters.map((ch) => (
                <div
                  key={ch.number}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors">
                      {ch.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </div>
              ))}
            </div>

            {comic.chapters > 10 && (
              <div className="text-center mt-6">
                <button className="text-sm text-primary-500 font-medium hover:text-primary-600">
                  查看全部 {comic.chapters} 话
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((rc) => (
                  <ComicCard
                    key={rc.id}
                    id={rc.id}
                    title={rc.title}
                    author={rc.author}
                    status={rc.status}
                    rating={rc.rating}
                    coverImage={rc.coverImage}
                    category={rc.category}
                    tags={rc.tags}
                    chapters={rc.chapters}
                    slug={rc.slug}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${comic.title} - 好看的韩国漫画`,
          description: comic.description,
          author: {
            '@type': 'Person',
            name: comic.author,
          },
          datePublished: '2024-01-01',
          dateModified: comic.updateDate,
          image: comic.coverImage,
          url: `https://www.example.com/article/${comic.slug}`,
          publisher: {
            '@type': 'Organization',
            name: '韩漫漫画网站',
          },
        })}
      </script>
    </>
  );
}